import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { resolveImageUrl } from './resolveImageUrl';

export interface ScreenshotLightboxImage {
  url: string;
  alt: string;
  title?: string;
}

export interface ScreenshotLightboxProps {
  /** The screenshot to enlarge, or null for closed. */
  image: ScreenshotLightboxImage | null;
  onClose: () => void;
}

/**
 * One screenshot, enlarged. Esc, a click outside the image, or "Luk" closes it. Mesterplan web has
 * no reusable lightbox (the only one is inline in DocumentsPanel), and one image per note means no
 * arrows. A darker scrim than `Dialog`'s so the screenshot reads as the subject (mockup .lbx).
 */
export function ScreenshotLightbox({ image, onClose }: ScreenshotLightboxProps) {
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!image) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    closeRef.current?.focus();
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [image, onClose]);

  if (!image) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={image.title ? `Skærmbillede: ${image.title}` : 'Skærmbillede'}
      data-testid="screenshot-lightbox"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-[rgba(10,14,22,0.82)] px-4 pb-6 pt-14"
      onClick={onClose}
    >
      <div className="relative aspect-[16/10] w-[min(960px,100%)] max-w-full" onClick={(event) => event.stopPropagation()}>
        <button
          ref={closeRef}
          type="button"
          onClick={onClose}
          className="absolute -top-10 right-0 rounded-lg bg-white px-3 py-1.5 text-[13px] font-medium text-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          Luk
        </button>
        <img src={resolveImageUrl(image.url)} alt={image.alt} className="h-full w-full rounded-md bg-surface object-contain dark:bg-dark-surface" />
      </div>
    </div>,
    document.body,
  );
}
